/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable react/no-array-index-key */
/* eslint-disable no-param-reassign */
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { debounce } from 'lodash';

type FractionFieldProps = {
	value: number;
	disabled: boolean;
	minValue: number;
	maxValue: number;
	isCoupled?: boolean;
	placeHolderLabel?: string;
	varName: string;
	onBlur: (newNumber: number) => void;
	width?: number;
	extraClasses?: string;
	inputClass?: string;
	selectClass?: string;
};

const getSeperatedNumbers = (val) => {
	const values = (val ?? '').toString().split('.');
	let wholeValue = 0;
	let fractionValue = 0;

	if (val !== null) {
		wholeValue = typeof Number(val) === 'number' ? parseInt(values[0], 10) : 0;
		fractionValue = parseFloat(`0.${values.length > 1 ? values[1] : '0'}`);
	} else {
		wholeValue = 0;
		fractionValue = 0;
	}

	return [wholeValue, fractionValue];
};

const roundToNearestFraction = (decimal) => {
	const fractions = [
		0.0625,
		0.125,
		0.1875,
		0.25,
		0.3125,
		0.375,
		0.4375,
		0.5,
		0.5625,
		0.625,
		0.6875,
		0.75,
		0.8125,
		0.875,
		0.9375,
		1
	];
	let lowerFraction = 0;
	let higherFraction = 1;

	for (let i = 0; i < fractions.length; i++) {
		if (decimal < (fractions[i] ?? 0)) {
			higherFraction = fractions[i] ?? 0;
			lowerFraction = i > 0 ? (fractions[i - 1] ?? 0) : 0;
			break;
		}
	}
	
	const roundedDown = lowerFraction;
	const roundUpThreshold = higherFraction - 0.0006;
	return decimal > roundUpThreshold ? higherFraction : roundedDown;
};

const FractionField = ({
	value,
	disabled,
	minValue,
	maxValue,
	isCoupled = false,
	placeHolderLabel = "-",
	varName,
	onBlur,
	width,
	extraClasses = "",
	inputClass = "",
	selectClass = ""

}: FractionFieldProps) => {
	const [displayValue, setDisplayValue] = useState(value);
	const [isEditting, setIsEditting] = useState(false);

	const [incomingWholeValue, displayFractionValue = 0] = getSeperatedNumbers(displayValue);
	const [displayWholeValue, setDisplayWholeValue] = useState(`${incomingWholeValue}`);

	const onBlurRef = useRef(onBlur);
	useEffect(() => {
		onBlurRef.current = onBlur;
	}, [onBlur]);

	const blurred = useCallback((newValue) => {
		onBlurRef.current(newValue);
	}, []);

	useEffect(() => {
		if (!isEditting) {
			setDisplayValue(value);
			const [whole] = getSeperatedNumbers(value);
			setDisplayWholeValue(`${whole}`);
		}
	}, [isEditting, value]);

	const textInputBlurred = (newVal) => {
		newVal = parseFloat(newVal);

		const min = minValue ?? Number.MIN_VALUE;

		const max = maxValue ?? Number.MAX_VALUE;

		if (newVal < min) {
			newVal = min;
		} else if (newVal > max) {
			newVal = max;
		}

		const [wholeValue = 0, fractionValue = 0] = getSeperatedNumbers(newVal);

		blurred(wholeValue + fractionValue);

		setDisplayValue(fractionValue + wholeValue);
	};

	const fractionChange = (newVal) => {
		textInputBlurred(Number(displayWholeValue) + newVal);
	};

	const fractionOptionsBase = [
		{ value: 0, displayValue: '0' },
		{ value: 0.0625, displayValue: '1/16' },
		{ value: 0.125, displayValue: '1/8' },
		{ value: 0.1875, displayValue: '3/16' },
		{ value: 0.25, displayValue: '1/4' },
		{ value: 0.3125, displayValue: '5/16' },
		{ value: 0.375, displayValue: '3/8' },
		{ value: 0.4375, displayValue: '7/16' },
		{ value: 0.5, displayValue: '1/2' },
		{ value: 0.5625, displayValue: '9/16' },
		{ value: 0.625, displayValue: '5/8' },
		{ value: 0.6875, displayValue: '11/16' },
		{ value: 0.75, displayValue: '3/4' },
		{ value: 0.8125, displayValue: '13/16' },
		{ value: 0.875, displayValue: '7/8' },
		{ value: 0.9375, displayValue: '15/16' },
	];

	const fractionOptions = fractionOptionsBase.map((fo) => {
		if (Math.abs(fo.value - (displayFractionValue ?? 0)) < 0.0006) {
			return {
				...fo,
				value: displayFractionValue
			};
		}

		return fo;
	});

	const options = fractionOptions.map((fo, index) => (
		<option key={index} value={fo.value}>
			{fo.displayValue}
		</option>
	));

	const wholeNumberChange = useCallback((newWholeNum, shouldStopEditing = true) => {
		const stringVal = (newWholeNum ?? "").toString();
		const hasDot = stringVal.includes('.');
		const valToParse = stringVal === "" ? 0 : newWholeNum;

		const [wholeNumber = 0, fractionNumber = 0] = getSeperatedNumbers(valToParse);
		const max = maxValue ?? Number.MAX_VALUE;
		const min = minValue ?? Number.MIN_VALUE;

		let finalWholeNumber = wholeNumber;
		const effectiveFraction = fractionNumber !== 0 ? roundToNearestFraction(fractionNumber) : displayFractionValue;

		if (wholeNumber > max) {
			if (effectiveFraction > 0) {
				finalWholeNumber = max - 1;
			} else {
				finalWholeNumber = max;
			}
		} else if (wholeNumber < min) {
			finalWholeNumber = min;
		}

		if (fractionNumber === 0) {
			if (hasDot) {
				blurred(finalWholeNumber);
			} else {
				blurred(finalWholeNumber + displayFractionValue);
			}
		} else {
			blurred(finalWholeNumber + effectiveFraction);
			if (shouldStopEditing) {
				setDisplayWholeValue(`${finalWholeNumber}`);
			}
		}
		if (shouldStopEditing) {
			setIsEditting(false);
		}
	}, [displayFractionValue, maxValue, minValue, blurred]);

	const debouncedWholeNumberChange = useMemo(
		() => debounce((val) => wholeNumberChange(val, false), 1000),
		[wholeNumberChange]
	);

	useEffect(() => () => {
		debouncedWholeNumberChange.cancel();
	}, [debouncedWholeNumberChange]);

	const inputMin = (minValue !== undefined && minValue !== null)
		? Math.ceil(minValue - displayFractionValue)
		: undefined;
	const inputMax = (maxValue !== undefined && maxValue !== null)
		? Math.floor(maxValue - displayFractionValue)
		: undefined;

	return (
		<div
			className={`${extraClasses} fraction-field d-flex justify-content-between align-items-center`}
			style={{ width: `${width}em`, minWidth: "100%" }}
		>
			<input
				className={`fraction-inner-whole form-control ${inputClass}`}
				min={inputMin}
				max={inputMax}
				style={{ flex: 1 }}
				onKeyDown={(e) => e.key === "Enter" && wholeNumberChange(displayWholeValue, false)}
				disabled={disabled}
				name={varName}
				type="number"
				value={displayWholeValue === "0" ? "" : displayWholeValue}
				onBlur={() => {
					debouncedWholeNumberChange.cancel();
					wholeNumberChange(displayWholeValue);
				}}
				onChange={(e) => {
					setIsEditting(true);
					setDisplayWholeValue(e.target.value);
					debouncedWholeNumberChange(e.target.value);
				}}
				placeholder={placeHolderLabel}
				// React typing doesn't understand that there is a blur method on the target
				onWheel={(e) => ((e.target) as unknown as { blur: (() => {}) })?.blur()}
			/>
			{isCoupled ? <br /> : null}
			<select
				className={`fraction-inner-select rounded form-select px-2 ${selectClass}`}
				style={{ flex: 1 }}
				disabled={disabled}
				value={displayFractionValue}
				onChange={(e) => {
					fractionChange(parseFloat(e.target.value));
				}}
				// React typing doesn't understand that there is a blur method on the target
				onWheel={(e) => ((e.target) as unknown as { blur: (() => {}) })?.blur()}
			>
				<option disabled hidden selected value={0}>-</option>
				{options}
			</select>
		</div>
	);
};

export default FractionField;
